import { useContext, useState, useMemo } from "react";
import { AlquileresContext } from "../../../../context/AlquileresContext";
import { saveGasto, deleteGasto } from "../../../../application/gastos";
import { formatMonto } from "../../../../utils/rentalCalculations";
import { FaPlusCircle, FaTrash, FaSearch, FaTimes } from "react-icons/fa";
import { useSortableData } from "../../../../hooks/useSortableData";
import SortableHeader from "../../../../components/SortableHeader";
import MoneyInput from "../../../../components/MoneyInput";

const CATEGORIAS = [
  "Plomeria",
  "Electricidad",
  "Pintura",
  "Cerrajeria",
  "Limpieza",
  "Gas",
  "Reparacion general",
  "Expensas",
  "Impuestos",
  "Otro",
];

const MESES = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

function Gastos() {
  const { contratosActivos, gastos, refreshGastos } = useContext(AlquileresContext);
  const now = new Date();
  const [mes, setMes] = useState(now.getMonth() + 1);
  const [anio, setAnio] = useState(now.getFullYear());
  const [search, setSearch] = useState("");
  const [modal, setModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    contratoId: "",
    descripcion: "",
    monto: "",
    fecha: new Date().toISOString().split("T")[0],
    categoria: "Reparacion general",
    pagadoPor: "PROPIETARIO",
  });

  // Gastos del mes seleccionado
  const gastosMes = useMemo(() => {
    return gastos
      .filter((g) => g.mes === mes && g.anio === anio)
      .filter((g) => {
        if (!search) return true;
        const s = search.toLowerCase();
        return (
          g.propiedad?.toLowerCase().includes(s) ||
          g.locadorNombre?.toLowerCase().includes(s) ||
          g.locatarioNombre?.toLowerCase().includes(s) ||
          g.descripcion?.toLowerCase().includes(s) ||
          g.categoria?.toLowerCase().includes(s)
        );
      });
  }, [gastos, mes, anio, search]);

  const { sortedData: sortedGastos, sortConfig, requestSort } = useSortableData(gastosMes, {
    storageKey: "sort-gastos",
    initial: { key: "fecha", direction: "desc" },
  });

  const totalGastosMes = useMemo(() => {
    return gastosMes.reduce((s, g) => s + (Number(g.monto) || 0), 0);
  }, [gastosMes]);

  // Por categoria
  const porCategoria = useMemo(() => {
    const map = {};
    gastosMes.forEach((g) => {
      const cat = g.categoria || "Otro";
      map[cat] = (map[cat] || 0) + (Number(g.monto) || 0);
    });
    return Object.entries(map).sort((a, b) => b[1] - a[1]);
  }, [gastosMes]);

  const openNuevo = () => {
    setForm({
      contratoId: "",
      descripcion: "",
      monto: "",
      fecha: new Date().toISOString().split("T")[0],
      categoria: "Reparacion general",
      pagadoPor: "PROPIETARIO",
    });
    setModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.contratoId || !form.monto || !form.descripcion) return;
    setSaving(true);
    try {
      const contrato = contratosActivos.find((c) => c.idFirebase === form.contratoId);
      await saveGasto({
        ...form,
        monto: Number(form.monto),
        mes,
        anio,
        locadorNombre: contrato?.locadorNombre || "",
        locatarioNombre: contrato?.locatarioNombre || "",
        propiedad: contrato?.propiedad || "",
        locadorId: contrato?.locadorId || "",
      });
      await refreshGastos();
      setModal(false);
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar este gasto?")) return;
    try {
      await deleteGasto(id);
      await refreshGastos();
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const inputClass =
    "w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 focus:ring-2 focus:ring-primary focus:border-transparent";

  return (
    <div className="px-4 md:px-6 py-4 space-y-4">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100">Gastos</h1>
        <button
          onClick={openNuevo}
          className="inline-flex items-center gap-2 bg-primary hover:bg-secondary text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors"
        >
          <FaPlusCircle className="w-4 h-4" /> Nuevo Gasto
        </button>
      </div>

      {/* Mes + Search */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="flex gap-2">
          <select value={mes} onChange={(e) => setMes(Number(e.target.value))} className="text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100">
            {MESES.map((m, i) => (<option key={i} value={i + 1}>{m}</option>))}
          </select>
          <select value={anio} onChange={(e) => setAnio(Number(e.target.value))} className="text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100">
            {[2024, 2025, 2026, 2027].map((a) => (<option key={a} value={a}>{a}</option>))}
          </select>
        </div>
        <div className="relative flex-1 max-w-sm">
          <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Buscar por propiedad, descripcion..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>
      </div>

      {/* Totales */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-4">
          <p className="text-xs text-gray-500 dark:text-gray-400">Total Gastos del Mes</p>
          <p className="text-2xl font-bold text-red-500">{formatMonto(totalGastosMes)}</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-4">
          <p className="text-xs text-gray-500 dark:text-gray-400">Cantidad</p>
          <p className="text-2xl font-bold text-gray-800 dark:text-gray-100">{gastosMes.length}</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-4">
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Por Categoria</p>
          {porCategoria.length === 0 ? (
            <p className="text-sm text-gray-400">-</p>
          ) : (
            <div className="space-y-1">
              {porCategoria.slice(0, 4).map(([cat, monto]) => (
                <div key={cat} className="flex justify-between text-xs">
                  <span className="text-gray-600 dark:text-gray-400">{cat}</span>
                  <span className="font-medium text-gray-800 dark:text-gray-200">{formatMonto(monto)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Tabla */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                <SortableHeader sortKey="fecha" currentKey={sortConfig.key} direction={sortConfig.direction} onSort={requestSort} align="left" className="px-4 py-3 font-semibold text-gray-600 dark:text-gray-400">Fecha</SortableHeader>
                <SortableHeader sortKey="propiedad" currentKey={sortConfig.key} direction={sortConfig.direction} onSort={requestSort} align="left" className="px-4 py-3 font-semibold text-gray-600 dark:text-gray-400">Propiedad</SortableHeader>
                <SortableHeader sortKey="locadorNombre" currentKey={sortConfig.key} direction={sortConfig.direction} onSort={requestSort} align="left" className="px-4 py-3 font-semibold text-gray-600 dark:text-gray-400">Propietario</SortableHeader>
                <SortableHeader sortKey="descripcion" currentKey={sortConfig.key} direction={sortConfig.direction} onSort={requestSort} align="left" className="px-4 py-3 font-semibold text-gray-600 dark:text-gray-400">Descripcion</SortableHeader>
                <SortableHeader sortKey="categoria" currentKey={sortConfig.key} direction={sortConfig.direction} onSort={requestSort} align="center" className="px-4 py-3 font-semibold text-gray-600 dark:text-gray-400">Categoria</SortableHeader>
                <SortableHeader sortKey="pagadoPor" currentKey={sortConfig.key} direction={sortConfig.direction} onSort={requestSort} align="center" className="px-4 py-3 font-semibold text-gray-600 dark:text-gray-400">Paga</SortableHeader>
                <SortableHeader sortKey="monto" currentKey={sortConfig.key} direction={sortConfig.direction} onSort={requestSort} align="right" className="px-4 py-3 font-semibold text-gray-600 dark:text-gray-400">Monto</SortableHeader>
                <th className="text-center px-4 py-3 font-semibold text-gray-600 dark:text-gray-400 w-16"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {sortedGastos.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-gray-400">
                    No hay gastos registrados en {MESES[mes - 1]} {anio}
                  </td>
                </tr>
              ) : (
                sortedGastos.map((g) => (
                  <tr key={g.idFirebase} className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-400 text-xs">{g.fecha}</td>
                    <td className="px-4 py-3 text-gray-800 dark:text-gray-200">{g.propiedad}</td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{g.locadorNombre}</td>
                    <td className="px-4 py-3 text-gray-800 dark:text-gray-200">{g.descripcion}</td>
                    <td className="px-4 py-3 text-center">
                      <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                        {g.categoria}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`text-xs font-medium ${
                        g.pagadoPor === "PROPIETARIO" ? "text-amber-600" : g.pagadoPor === "INQUILINO" ? "text-blue-600" : "text-violet-600"
                      }`}>
                        {g.pagadoPor === "PROPIETARIO" ? "Propietario" : g.pagadoPor === "INQUILINO" ? "Inquilino" : "Inmobiliaria"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right font-bold text-red-500">
                      {formatMonto(g.monto)}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button onClick={() => handleDelete(g.idFirebase)} className="text-gray-400 hover:text-red-500 transition-colors">
                        <FaTrash className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal Nuevo Gasto */}
      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-2xl mx-4 p-8">
            <div className="flex items-center justify-between pb-4 mb-5 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100">Nuevo Gasto</h3>
              <button onClick={() => setModal(false)} className="p-2 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                <FaTimes />
              </button>
            </div>
            <form onSubmit={handleSave} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Propiedad / Contrato *</label>
                <select value={form.contratoId} onChange={(e) => setForm({ ...form, contratoId: e.target.value })} className={inputClass} required>
                  <option value="">Seleccionar...</option>
                  {contratosActivos.map((c) => (
                    <option key={c.idFirebase} value={c.idFirebase}>
                      {c.propiedad} - {c.locadorNombre} / {c.locatarioNombre}
                    </option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Categoria</label>
                  <select value={form.categoria} onChange={(e) => setForm({ ...form, categoria: e.target.value })} className={inputClass}>
                    {CATEGORIAS.map((c) => (<option key={c} value={c}>{c}</option>))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Quien paga</label>
                  <select value={form.pagadoPor} onChange={(e) => setForm({ ...form, pagadoPor: e.target.value })} className={inputClass}>
                    <option value="PROPIETARIO">Propietario (se descuenta)</option>
                    <option value="INMOBILIARIA">Inmobiliaria</option>
                    <option value="INQUILINO">Inquilino</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Descripcion *</label>
                <input type="text" value={form.descripcion} onChange={(e) => setForm({ ...form, descripcion: e.target.value })} className={inputClass} placeholder="Ej: Arreglo canilla cocina" required />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Monto *</label>
                  <MoneyInput value={form.monto} onChange={(v) => setForm({ ...form, monto: v })} className={inputClass} required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Fecha</label>
                  <input type="date" value={form.fecha} onChange={(e) => setForm({ ...form, fecha: e.target.value })} className={inputClass} />
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4 mt-2 border-t border-gray-100 dark:border-gray-700">
                <button type="button" onClick={() => setModal(false)} className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">Cancelar</button>
                <button type="submit" disabled={saving} className="px-4 py-2 text-sm bg-primary hover:bg-secondary text-white rounded-lg disabled:opacity-50">
                  {saving ? "Guardando..." : "Guardar Gasto"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Gastos;
